import {
  bulkVaultShare,
  getDirectoryRoleSharesByDirectory,
  getDirectoryRoleSharesByRole,
  getDirectorySharesByDirectory,
  getDirectorySharesByUser,
  getFileRoleSharesByFile,
  getFileRoleSharesByRole,
  getFileSharesByFile,
  getFileSharesByUser,
  removeDirectoryRoleShare,
  removeDirectoryShare,
  removeFileRoleShare,
  removeFileShare,
  upsertDirectoryRoleShare,
  upsertDirectoryShare,
  upsertFileRoleShare,
  upsertFileShare,
  type AccessPermission,
  type ApplicationMessage,
  type PermissionRoleInDb,
  type UserInDb,
  type VaultDirectoryRoleShareInDb,
  type VaultDirectoryUserShareInDb,
  type VaultFileRoleShareInDb,
  type VaultFileUserShareInDb,
} from '@/api';
import { toAccessPermission } from '@/utils/accessPermission';
import { ContractShareService } from '@/services/contractShareService';
import { extractErrorDetail } from '@/utils/errorHandler';

/** Run a call, rethrowing its failure as an Error carrying the backend's detail. */
async function call<T>(request: () => Promise<{ data: T }>, fallback: string): Promise<T> {
  try {
    const { data } = await request();
    return data;
  } catch (error) {
    throw new Error(extractErrorDetail(error, fallback));
  }
}

/**
 * Something in the vault that can be shared. Folders, plain files and
 * contracts each keep their own shares; a folder's grants are inherited by
 * everything below it. A contract's own files have none (the contract
 * governs them), and nothing in the system vault or a vault root can be
 * shared — the backend refuses those with a 400.
 */
export interface VaultShareTarget {
  kind: 'file' | 'directory' | 'contract';
  uid: string;
}

/** One direct share on a vault item, whoever it is with. */
export interface VaultShareEntry {
  user: UserInDb | null;
  role: PermissionRoleInDb | null;
  permissions: AccessPermission;
}

function userEntry(share: { user: UserInDb; permissions: AccessPermission }): VaultShareEntry {
  return { user: share.user, role: null, permissions: share.permissions };
}

function roleEntry(share: {
  role: PermissionRoleInDb;
  permissions: AccessPermission;
}): VaultShareEntry {
  return { user: null, role: share.role, permissions: share.permissions };
}

/**
 * Shares on vault files and folders, plus the same calls for contracts so a
 * vault surface can treat all three alike (contracts delegate to
 * `ContractShareService`).
 */
export const VaultShareService = {
  /** The users and roles an item is shared with directly (not what it inherits). */
  async getShares(target: VaultShareTarget): Promise<VaultShareEntry[]> {
    const fallback = 'Failed to load shares. Please try again.';
    if (target.kind === 'contract') {
      const [users, roles] = await Promise.all([
        ContractShareService.getUserSharesByContract(target.uid),
        ContractShareService.getRoleSharesByContract(target.uid),
      ]);
      return [...users.map(userEntry), ...roles.map(roleEntry)];
    }
    if (target.kind === 'file') {
      const path = { file_uid: target.uid };
      const [users, roles] = await Promise.all([
        call(() => getFileSharesByFile({ path, throwOnError: true }), fallback),
        call(() => getFileRoleSharesByFile({ path, throwOnError: true }), fallback),
      ]);
      return [...users.map(userEntry), ...roles.map(roleEntry)];
    }
    const path = { directory_uid: target.uid };
    const [users, roles] = await Promise.all([
      call(() => getDirectorySharesByDirectory({ path, throwOnError: true }), fallback),
      call(() => getDirectoryRoleSharesByDirectory({ path, throwOnError: true }), fallback),
    ]);
    return [...users.map(userEntry), ...roles.map(roleEntry)];
  },

  /** Grant or change a user's access; sharing a pair again overwrites its level. */
  async setUserShare(
    target: VaultShareTarget,
    userUid: string,
    permissions: number
  ): Promise<void> {
    const fallback = 'Failed to update permissions. Please try again.';
    const body = { permissions: toAccessPermission(permissions) };
    if (target.kind === 'contract') {
      await ContractShareService.updateUserSharePermissions(target.uid, userUid, permissions);
    } else if (target.kind === 'file') {
      await call(
        () =>
          upsertFileShare({
            path: { file_uid: target.uid, user_uid: userUid },
            body,
            throwOnError: true,
          }),
        fallback
      );
    } else {
      await call(
        () =>
          upsertDirectoryShare({
            path: { directory_uid: target.uid, user_uid: userUid },
            body,
            throwOnError: true,
          }),
        fallback
      );
    }
  },

  /** Grant or change a role's access; sharing a pair again overwrites its level. */
  async setRoleShare(
    target: VaultShareTarget,
    roleUid: string,
    permissions: number
  ): Promise<void> {
    const fallback = 'Failed to update permissions. Please try again.';
    const body = { permissions: toAccessPermission(permissions) };
    if (target.kind === 'contract') {
      await ContractShareService.updateRoleSharePermissions(target.uid, roleUid, permissions);
    } else if (target.kind === 'file') {
      await call(
        () =>
          upsertFileRoleShare({
            path: { file_uid: target.uid, role_uid: roleUid },
            body,
            throwOnError: true,
          }),
        fallback
      );
    } else {
      await call(
        () =>
          upsertDirectoryRoleShare({
            path: { directory_uid: target.uid, role_uid: roleUid },
            body,
            throwOnError: true,
          }),
        fallback
      );
    }
  },

  async removeUserShare(target: VaultShareTarget, userUid: string): Promise<void> {
    const fallback = 'Failed to remove user share. Please try again.';
    if (target.kind === 'contract') {
      await ContractShareService.removeUserShare(target.uid, userUid);
    } else if (target.kind === 'file') {
      await call(
        () =>
          removeFileShare({
            path: { file_uid: target.uid, user_uid: userUid },
            throwOnError: true,
          }),
        fallback
      );
    } else {
      await call(
        () =>
          removeDirectoryShare({
            path: { directory_uid: target.uid, user_uid: userUid },
            throwOnError: true,
          }),
        fallback
      );
    }
  },

  async removeRoleShare(target: VaultShareTarget, roleUid: string): Promise<void> {
    const fallback = 'Failed to remove role share. Please try again.';
    if (target.kind === 'contract') {
      await ContractShareService.removeRoleShare(target.uid, roleUid);
    } else if (target.kind === 'file') {
      await call(
        () =>
          removeFileRoleShare({
            path: { file_uid: target.uid, role_uid: roleUid },
            throwOnError: true,
          }),
        fallback
      );
    } else {
      await call(
        () =>
          removeDirectoryRoleShare({
            path: { directory_uid: target.uid, role_uid: roleUid },
            throwOnError: true,
          }),
        fallback
      );
    }
  },

  /** Share plain files and folders with users and/or roles in one go. */
  bulkShare(params: {
    fileUids: string[];
    directoryUids: string[];
    userUids: string[];
    roleUids: string[];
    permissions: number;
  }): Promise<ApplicationMessage> {
    return call(
      () =>
        bulkVaultShare({
          body: {
            file_uids: params.fileUids,
            directory_uids: params.directoryUids,
            user_uids: params.userUids,
            role_uids: params.roleUids,
            permissions: toAccessPermission(params.permissions),
          },
          throwOnError: true,
        }),
      'Failed to share. Please try again.'
    );
  },

  // ── By user / role (the people pages) ──────────────────────────────────────

  getFileSharesByUser(userUid: string): Promise<VaultFileUserShareInDb[]> {
    return call(
      () => getFileSharesByUser({ path: { user_uid: userUid }, throwOnError: true }),
      'Failed to load files shared with this user. Please try again.'
    );
  },

  getDirectorySharesByUser(userUid: string): Promise<VaultDirectoryUserShareInDb[]> {
    return call(
      () => getDirectorySharesByUser({ path: { user_uid: userUid }, throwOnError: true }),
      'Failed to load folders shared with this user. Please try again.'
    );
  },

  getFileSharesByRole(roleUid: string): Promise<VaultFileRoleShareInDb[]> {
    return call(
      () => getFileRoleSharesByRole({ path: { role_uid: roleUid }, throwOnError: true }),
      'Failed to load files shared with this role. Please try again.'
    );
  },

  getDirectorySharesByRole(roleUid: string): Promise<VaultDirectoryRoleShareInDb[]> {
    return call(
      () => getDirectoryRoleSharesByRole({ path: { role_uid: roleUid }, throwOnError: true }),
      'Failed to load folders shared with this role. Please try again.'
    );
  },
};
